import type { ITask, ITaskDependency } from "../types";

export interface StackGroup {
  /** All task UUIDs in the connected component, ordered for display (top→bottom). */
  taskUuids: string[];
  /** The task with no dependents (the ultimate goal). Used for list anchor. */
  rootUuid: string;
  /** Tasks with no incomplete prerequisites remaining — eligible to move to Today. */
  eligibleUuids: string[];
}

/**
 * Group a list of tasks into visual stacks based on active dependencies.
 *
 * Rules:
 *  - Only dependencies where BOTH ends appear in `tasks` form a visible stack.
 *  - Connected components (via prerequisite/dependent edges) become one stack.
 *  - Within each stack: topologically order top→bottom so "most prerequisite" tasks
 *    appear first (top of stack) and the "root" (no outgoing-dependent edges) at the bottom.
 *  - The anchor (`rootUuid`) is the task in the component with no outgoing dependent edges
 *    that lies within the visible task set. If multiple, the first encountered wins.
 *  - `eligibleUuids`: tasks whose prerequisites (within the full dependency graph, not just
 *    the visible set) are all completed/missing → eligible to move to Today.
 */
export function groupTasksIntoStacks(
  tasks: ITask[],
  dependencies: ITaskDependency[],
  allTasks: ITask[]
): {
  stacks: Map<string, StackGroup>;
  taskToStack: Map<string, string>;
} {
  const taskUuidSet = new Set(tasks.map((t) => t.uuid));
  const allTasksByUuid = new Map(allTasks.map((t) => [t.uuid, t]));

  // Filter: only active deps where both endpoints are in the visible list
  const visibleDeps = dependencies.filter(
    (d) =>
      taskUuidSet.has(d.prerequisite_uuid) &&
      taskUuidSet.has(d.dependent_uuid)
  );

  // Build adjacency maps
  const outEdges = new Map<string, string[]>(); // prereq -> dependents
  const inEdges = new Map<string, string[]>(); // dependent -> prereqs
  for (const d of visibleDeps) {
    if (!outEdges.has(d.prerequisite_uuid)) outEdges.set(d.prerequisite_uuid, []);
    outEdges.get(d.prerequisite_uuid)!.push(d.dependent_uuid);
    if (!inEdges.has(d.dependent_uuid)) inEdges.set(d.dependent_uuid, []);
    inEdges.get(d.dependent_uuid)!.push(d.prerequisite_uuid);
  }

  // Find connected components (undirected) among linked tasks
  const linkedTaskUuids = new Set<string>();
  for (const d of visibleDeps) {
    linkedTaskUuids.add(d.prerequisite_uuid);
    linkedTaskUuids.add(d.dependent_uuid);
  }

  const visited = new Set<string>();
  const stacks = new Map<string, StackGroup>();
  const taskToStack = new Map<string, string>();

  for (const startUuid of linkedTaskUuids) {
    if (visited.has(startUuid)) continue;

    // BFS to find the full connected component
    const component = new Set<string>();
    const queue: string[] = [startUuid];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      if (component.has(cur)) continue;
      component.add(cur);
      for (const n of outEdges.get(cur) || []) {
        if (!component.has(n)) queue.push(n);
      }
      for (const n of inEdges.get(cur) || []) {
        if (!component.has(n)) queue.push(n);
      }
    }

    for (const uuid of component) visited.add(uuid);

    // Topologically sort component: tasks with no prereqs (within component) come first (top)
    const componentArr = Array.from(component);
    const inDegree = new Map<string, number>();
    for (const uuid of componentArr) {
      const prereqs = (inEdges.get(uuid) || []).filter((p) => component.has(p));
      inDegree.set(uuid, prereqs.length);
    }

    const sorted: string[] = [];
    const ready: string[] = componentArr
      .filter((uuid) => (inDegree.get(uuid) || 0) === 0)
      .sort((a, b) => {
        const ta = allTasksByUuid.get(a);
        const tb = allTasksByUuid.get(b);
        return (ta?.created_at || "").localeCompare(tb?.created_at || "");
      });

    while (ready.length > 0) {
      const cur = ready.shift()!;
      sorted.push(cur);
      for (const next of (outEdges.get(cur) || []).filter((n) => component.has(n))) {
        const d = (inDegree.get(next) || 0) - 1;
        inDegree.set(next, d);
        if (d === 0) {
          const curTask = allTasksByUuid.get(next);
          let inserted = false;
          for (let i = 0; i < ready.length; i++) {
            const readyTask = allTasksByUuid.get(ready[i]);
            if ((curTask?.created_at || "") < (readyTask?.created_at || "")) {
              ready.splice(i, 0, next);
              inserted = true;
              break;
            }
          }
          if (!inserted) ready.push(next);
        }
      }
    }

    // Root (bottom of stack) = task with no outgoing edges within component
    const root =
      sorted.slice().reverse().find((uuid) => {
        const outs = (outEdges.get(uuid) || []).filter((n) => component.has(n));
        return outs.length === 0;
      }) || sorted[sorted.length - 1];

    // Eligibility: a task is eligible to move to Today if all of its active prereqs
    // (across the full dep graph, not just visible) are completed or missing.
    const eligibleUuids: string[] = [];
    for (const uuid of sorted) {
      const task = allTasksByUuid.get(uuid);
      if (!task) continue;
      const prereqs = dependencies.filter((d) => d.dependent_uuid === uuid);
      const allDone = prereqs.every((d) => {
        const prereqTask = allTasksByUuid.get(d.prerequisite_uuid);
        return !prereqTask || prereqTask.status === "done" || prereqTask.status === "archived";
      });
      if (allDone) eligibleUuids.push(uuid);
    }

    const stack: StackGroup = {
      taskUuids: sorted,
      rootUuid: root,
      eligibleUuids,
    };
    const anchorKey = root;
    stacks.set(anchorKey, stack);
    for (const uuid of sorted) taskToStack.set(uuid, anchorKey);
  }

  return { stacks, taskToStack };
}

/**
 * Get the incomplete prerequisite tasks for a given task (transitive).
 * Returns tasks whose status is not 'done' or 'archived'.
 */
export function getIncompletePrerequisites(
  taskUuid: string,
  dependencies: ITaskDependency[],
  allTasks: ITask[]
): ITask[] {
  const byUuid = new Map(allTasks.map((t) => [t.uuid, t]));
  const result: ITask[] = [];
  const seen = new Set<string>();
  const stack: string[] = [taskUuid];

  while (stack.length > 0) {
    const cur = stack.pop()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    const prereqs = dependencies.filter((d) => d.dependent_uuid === cur);
    for (const d of prereqs) {
      const prereqTask = byUuid.get(d.prerequisite_uuid);
      if (!prereqTask) continue;
      if (prereqTask.status === "done" || prereqTask.status === "archived") continue;
      if (!result.some((t) => t.uuid === prereqTask.uuid)) {
        result.push(prereqTask);
      }
      stack.push(prereqTask.uuid);
    }
  }

  return result;
}

/**
 * Check if a task has any active dependencies (as prereq or dependent).
 */
export function hasAnyDependencies(
  taskUuid: string,
  dependencies: ITaskDependency[]
): boolean {
  return dependencies.some(
    (d) => d.prerequisite_uuid === taskUuid || d.dependent_uuid === taskUuid
  );
}

/**
 * Check if a task is eligible to be moved to Today: no incomplete prereqs.
 */
export function isEligibleForToday(
  taskUuid: string,
  dependencies: ITaskDependency[],
  allTasks: ITask[]
): boolean {
  return getIncompletePrerequisites(taskUuid, dependencies, allTasks).length === 0;
}
